import {
  v2_getOrderMinValue,
  v2_useCart,
  v2_useChangeItemQuantity,
} from '@crocodille/common';
import React, { useCallback, useMemo, useRef } from 'react';
import { analyticsEvent } from '../../../modules/analytics';
import CartList, { CartListItem } from '../cart-list/cart-list';

const Cart: React.FC<
  React.PropsWithChildren<{ children?: never; readonly readonly?: boolean }>
> = ({ readonly = false }) => {
  const { data: order, isValidating } = v2_useCart();
  const { trigger: changeItemQuantity } = v2_useChangeItemQuantity();

  const orderRef = useRef(order);
  orderRef.current = order;

  const minOrderValue = useMemo(() => v2_getOrderMinValue(order), [order]);

  const handleQuantityChange = useCallback(
    async (id: number, quantity: number) => {
      await changeItemQuantity({ orderItemId: id, quantity });
      const item = orderRef.current?.items?.find?.((item) => item.id === id);

      const changeInQuantity = quantity - (item?.quantity ?? 0);

      if (changeInQuantity > 0) {
        analyticsEvent('add_to_cart', {
          ecommerce: {
            items: [
              {
                item_id: item?.code ?? '',
                item_name: item?.name ?? '',
                price: ((item?.price ?? 0) / 100).toFixed(2),
                item_category: '', // NOTE: Data is not available
                item_list_name: '', // NOTE: Data is not available
                quantity: changeInQuantity.toString(),
                index: 1,
              },
            ],
          },
        });

        analyticsEvent('remarketing_add_to_cart', {
          value: (item?.price ?? 0) / 100,
          items: [
            {
              id: item?.code ?? '',
              google_business_vertical: 'retail',
              quantity: changeInQuantity,
            },
          ],
        });
      } else if (changeInQuantity < 0) {
        analyticsEvent('remove_from_cart', {
          ecommerce: {
            items: [
              {
                item_id: item?.code ?? '',
                item_name: item?.name ?? '',
                price: ((item?.price ?? 0) / 100).toFixed(2),
                item_category: '', // NOTE: Data is not available
                item_list_name: '', // NOTE: Data is not available
                quantity: Math.abs(changeInQuantity).toString(),
                index: 1,
              },
            ],
          },
        });

        analyticsEvent('remarketing_remove_from_cart', {
          value: (item?.price ?? 0) / 100,
          items: [
            {
              id: item?.code ?? '',
              google_business_vertical: 'retail',
              quantity: Math.abs(changeInQuantity),
            },
          ],
        });
      }
    },
    [changeItemQuantity],
  );

  return (
    <CartList
      total={order?.total ?? 0}
      minOrderValue={minOrderValue}
      busy={isValidating}
      disabled={(order?.items?.length ?? 0) === 0}
      onQuantityChange={handleQuantityChange}
      onRemove={(id) => changeItemQuantity({ orderItemId: id, quantity: 0 })}
    >
      {order?.items?.map?.((item) => (
        <React.Fragment key={item.id}>
          <CartListItem
            productName={item.name}
            orderItemId={item.id}
            quantity={item.quantity}
            editDisabled={readonly}
            price={item.price}
            child={false}
          />
          {item.adjustments.map((adjustment) => (
            <CartListItem
              key={adjustment.id}
              orderItemId={item.id}
              productName={adjustment.name}
              quantity={adjustment.quantity ?? undefined}
              price={
                ['pastry'].includes(adjustment.role)
                  ? adjustment.price
                  : undefined
              }
              editDisabled={true}
              child={true}
            />
          ))}
          {/* {item.pastry ? (
            <CartListItem
              orderItemId={item.id}
              productName={item.pastry.name}
              price={item.pastry.price}
              editDisabled={true}
              child={true}
            />
          ) : null}
          {item.menu
            ? item.menu.map((menuItem) => (
                <CartListItem
                  key={menuItem.label}
                  orderItemId={item.id}
                  productName={menuItem.label}
                  quantity={menuItem.quantity}
                  price={menuItem.amount === 0 ? null : menuItem.amount}
                  editDisabled={true}
                  child={true}
                />
              ))
            : null} */}
        </React.Fragment>
      ))}
      {/* {cartItems?.flatMap?.((item) =>
        item.promotions.map((promotion) => (
          <CartListItem
            key={'promotion-' + item.id + promotion.type}
            productName={promotion.label}
            orderItemId={item.id}
            editDisabled={true}
            quantity={promotion.quantity}
            price={promotion.amount}
            child={false}
          />
        )),
      )} */}
      {order?.adjustments?.map?.((adjustment) => (
        <CartListItem
          key={adjustment.id}
          productName={adjustment.name}
          editDisabled={true}
          price={adjustment.price}
          child={false}
        />
      ))}
    </CartList>
  );
};

export default Cart;
